/* eslint-disable react/jsx-curly-newline */
import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Typography,
  DatePicker,
  Checkbox,
  Input,
  Button,
  Radio,
  Spin,
  Select,
  Form,
  Col,
  Row,
  message,
} from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import moment from 'moment';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { RadioChangeEvent } from 'antd/lib/radio';
import API from '../../../api/API';
import UserContext from '../../../contexts/user.context';
import FormDataContext from '../../../contexts/formData.context';
import { LoadingScreen, EventVersions } from './index';
import { IState } from '../../../models/state';
import { IFormat } from '../../../models/format';
import { IAgency } from '../../../models/agency';
import { IAudience } from '../../../models/audience';
import { IStaff } from '../../../models/staff';
import { ICongressMember } from '../../../models/congressMember';
import { ICounty } from '../../../models/county';
import { IEventRevision } from '../../../models/eventRevision';
import { IEventRevisionWithDetails } from '../../../models/eventRevisionWithDetails';
import './Event.css';

const { TextArea } = Input;
const { Title, Text } = Typography;

interface IProps {
  isEdit?: boolean;
}

const Event: React.FC<IProps> = (props: IProps) => {
  // #region State, Props, etc.

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedJurisdictions, setSelectedJurisdictions] = useState<IState[]>(
    [],
  );
  const [congressMembers, setCongressMembers] = useState<ICongressMember[]>([]);
  const [congressMembersValues, setCongressMembersValues] = useState<
    ICongressMember[]
  >([]);
  const [fetchingCongressMembers, setFetchingCongressMembers] = useState(false);
  const [dateRequired, setDateRequired] = useState(true);
  const [physicalRequired, setPhysicalRequired] = useState(false);
  const [otherAgencyRequired, setOtherAgencyRequired] = useState(false);
  const [otherAudienceRequired, setOtherAudienceRequired] = useState(false);
  const [jurisdictionRequired, setJurisdictionRequired] = useState(false);
  const [eventRevisions, setEventRevisions] = useState<IEventRevision[]>([]);
  const [
    eventDetails,
    setEventDetails,
  ] = useState<IEventRevisionWithDetails | null>(null);

  const { isAdmin } = useContext(UserContext);
  const {
    agencies,
    audiences,
    formats,
    states,
    principal,
    additional,
    region,
    jurisdictions,
  } = useContext(FormDataContext);

  const { isEdit } = props;

  const history = useHistory();
  const { id } = useParams<{ id?: string | undefined }>();

  // #endregion

  // #region Functions To Get Event Revision And Update State

  /*
  Returns values to populate event location section
  */
  const getEventLocation = (location: any, stateAbbreviation: any) => {
    let eventLocationRadioValue = '';
    let eventCity = '';
    let eventState = '';

    if (stateAbbreviation !== null) {
      eventLocationRadioValue = 'physical';
      eventCity = location;
      eventState = stateAbbreviation;
      setPhysicalRequired(true);
    } else if (location === 'Virtual/Other') {
      eventLocationRadioValue = 'virtual';
    } else {
      eventLocationRadioValue = 'tbd';
    }

    return [eventLocationRadioValue, eventCity, eventState];
  };

  /*
  Returns values to populate participating agencies section
  */
  const getEventAgencies = (values: any[]) => {
    const eventAgencies: string[] = [];
    let eventOtherAgency: string = '';

    values.forEach((a: any) => {
      if (a.userDefined === '1') {
        eventOtherAgency = a.agency;
        eventAgencies.push('Other');
        setOtherAgencyRequired(false);
      } else {
        eventAgencies.push(a.agency);
      }
    });

    return [eventAgencies, eventOtherAgency];
  };

  /*
  Returns values to populate audience(s) section
  */
  const getEventAudience = (values: any[]) => {
    const eventAudience: string[] = [];
    let eventOtherAudience: string = '';

    values.forEach((a: any) => {
      if (a.userDefined === '1') {
        eventOtherAudience = a.type;
        eventAudience.push('Other');
        setOtherAudienceRequired(false);
      } else {
        eventAudience.push(a.type);
      }
    });

    return [eventAudience, eventOtherAudience];
  };

  /*
  Returns values to populate attendees & speakers section
  */
  const getEventStaff = (values: any[]): string[] => {
    const eventStaff: string[] = [];

    values.forEach((st: any) => {
      eventStaff.push(`${st.staffOrg}|${st.staff}`);
    });

    return eventStaff;
  };

  /*
  Returns values to populate members of congress attending section
  */
  const getEventCongressMembers = (values: any[]): ICongressMember[] => {
    const eventCongressMembers: ICongressMember[] = [];

    values.forEach((cm: ICongressMember) => {
      if (cm.id !== null) {
        const currentCongressMember = cm;

        if (currentCongressMember.chamber === 'Senate') {
          currentCongressMember.chamber = 'Sen';
        } else {
          currentCongressMember.chamber = 'Rep';
        }

        eventCongressMembers.push(currentCongressMember);
      }
    });

    if (eventCongressMembers.length > 0) {
      setCongressMembersValues(eventCongressMembers);
    }

    return eventCongressMembers;
  };

  /*
  Returns values to populate ehe target jurisdiction section
  */
  const getEventJurisdictions = useCallback(
    (values: any[]) => {
      const eventJurisdictionStates = new Set<number>();
      const eventJurisdictionCounties: number[] = [];
      const newJurisdictions: IState[] = [];

      values.forEach((jd: any) => {
        if (jd.stateId !== null && jd.jurisdictionId !== null) {
          eventJurisdictionStates.add(jd.stateId);
          eventJurisdictionCounties.push(jd.jurisdictionId);
        }
      });

      if (eventJurisdictionStates.size > 0) {
        setJurisdictionRequired(true);

        eventJurisdictionStates.forEach((jid: any) => {
          const currentJurisdiction = jurisdictions.find(
            (st: IState) => st.id === jid,
          );
          if (currentJurisdiction !== undefined) {
            newJurisdictions.push(currentJurisdiction);
          }
        });

        setSelectedJurisdictions(newJurisdictions);
      }

      return [Array.from(eventJurisdictionStates), eventJurisdictionCounties];
    },
    [jurisdictions],
  );

  /*
  Updates the current event details by querying the server and updating current state
  */
  const getEventRevision = useCallback(
    async (revisionId: number) => {
      const resp = await API.get(`/event/${id}/${revisionId}`);
      const eventRevision = resp.data[0];

      if (eventRevision.occurredOn === null) setDateRequired(false);

      const [eventLocationRadioValue, eventCity, eventState] = getEventLocation(
        eventRevision.location,
        eventRevision.stateAbbreviation,
      );
      const [eventAgencies, eventOtherAgency] = getEventAgencies(
        eventRevision.agencies,
      );
      const [eventAudience, eventOtherAudience] = getEventAudience(
        eventRevision.audience,
      );
      const eventStaff = getEventStaff(eventRevision.staff);
      const eventCongressMembers = getEventCongressMembers(
        eventRevision.congressMembers,
      );
      const [
        eventJurisdictionStates,
        eventJurisdictionCounties,
      ] = getEventJurisdictions(eventRevision.jurisdictions);

      const eventWithDetails: IEventRevisionWithDetails = {
        id: eventRevision.id,
        eventId: eventRevision.eventId,
        createdOn: eventRevision.createdOn,
        createdBy: eventRevision.createdBy,
        occurredOn: eventRevision.occurredOn,
        description: eventRevision.description,
        format: eventRevision.format,
        additionalAttendees: eventRevision.additionalAttendees,
        jurisdictionVisit: eventRevision.jurisdictionVisit,
        readySetPrep: eventRevision.readySetPrep,
        locationRadioValue: eventLocationRadioValue,
        city: eventCity,
        state: eventState,
        otherLocalitiesReached: eventRevision.otherLocalitiesReached,
        agencies: eventAgencies,
        otherAgency: eventOtherAgency,
        audience: eventAudience,
        otherAudience: eventOtherAudience,
        staff: eventStaff,
        congressMembers: eventCongressMembers,
        jurisdictionStates: eventJurisdictionStates,
        jurisdictionCounties: eventJurisdictionCounties,
      };

      setEventDetails(eventWithDetails);
    },
    [id, getEventJurisdictions],
  );

  // #endregion

  // #region useEffect

  useEffect(() => {
    const title = isEdit
      ? 'EHE Tracker - Edit Event'
      : 'EHE Tracker - Submit Event';

    document.title = title;
  }, [isEdit]);

  useEffect(() => {
    const fetchEventRevisions = async () => {
      try {
        setLoading(true);

        const resp = await API.get(`/event/${id}`);
        const revisionId = resp.data[resp.data.length - 1].id;
        setEventRevisions(resp.data);
        await getEventRevision(revisionId);

        setLoading(false);
      } catch {
        history.push('/error');
      }
    };

    try {
      if (isEdit) {
        fetchEventRevisions();
      } else {
        setLoading(false);
      }
    } catch (e) {
      setLoading(true);
      message.error('Looks like something went wrong.');
    }
  }, [id, isEdit, history, getEventRevision]);

  // #endregion

  // #region Functions To Create Event Revision And Post To Server

  /*
  Creates an eventRevision object for the body of the API post request to submit form.
  */
  const createEventRevision = (values: any) => {
    /*
    Checks to see if form fields have value. If undefined, replaces with null.
    */
    const occurredOn =
      values.occurredOn === undefined || values.occurredOn === null
        ? null
        : values.occurredOn.format('MM-DD-YYYY');

    const additionalAttendees =
      values.additionalAttendees === undefined ||
      values.additionalAttendees.trim().length === 0
        ? null
        : values.additionalAttendees;

    let location = null;
    let stateAbbreviation = null;

    const otherLocalitiesReached =
      values.otherLocalitiesReached === undefined ||
      values.otherLocalitiesReached.trim().length === 0
        ? null
        : values.otherLocalitiesReached;

    if (values.locationRadio === 'physical') {
      location = values.location;
      stateAbbreviation = values.stateAbbreviation;
    } else if (values.locationRadio === 'virtual') {
      location = 'Virtual/Other';
    }

    /*
    Gets the rest of the form fields from the values object.
    */
    const { description, format, jurisdictionVisit, readySetPrep } = values;

    /*
    Creates and returns the eventRevision object.
    */
    const eventRevision = {
      occurredOn,
      description,
      format,
      additionalAttendees,
      jurisdictionVisit,
      readySetPrep,
      location,
      stateAbbreviation,
      otherLocalitiesReached,
    };

    return eventRevision;
  };

  /*
  Creates an eventAgencies object for the body of the API post request to submit form.
  */
  const createEventAgencies = (agenciesArray: string[], other: string) => {
    const eventAgencies = agenciesArray.map((a: string) =>
      a === 'Other' ? { userDefined: 1, agency: other } : { agency: a },
    );

    return eventAgencies;
  };

  /*
  Creates an eventAudience object for the body of the API post request to submit form.
  */
  const createEventAudiece = (audienceArray: string[], other: string) => {
    const eventAgencies = audienceArray.map((a: string) =>
      a === 'Other' ? { userDefined: 1, type: other } : { type: a },
    );

    return eventAgencies;
  };

  /*
  Creates an eventStaff object for the body of the API post request to submit form.
  */
  const createEventStaff = (eventStaffArray: string[]) => {
    const eventStaff: any[] = [];

    eventStaffArray.forEach((st: string) => {
      const staff = st.split('|');
      eventStaff.push({ staffOrg: staff[0], staff: staff[1] });
    });

    return eventStaff;
  };

  /*
  Creates the data object for the body of the API post request to submit form.
  */
  const createFormData = (values: any) => {
    const event: any = {
      lastUpdatedOn: new Date(),
      lastUpdatedBy: 'React App',
      createdOn: new Date(),
      createdBy: 'React App',
    };

    if (isEdit) event.id = id;

    const eventRevision = createEventRevision(values);
    const eventAgencies = createEventAgencies(
      values.eventAgencies,
      values.agencyOther,
    );
    const eventAudience = createEventAudiece(
      values.eventAudience,
      values.audienceOther,
    );
    const eventStaff = createEventStaff(values.eventStaff);
    const eventCongressMembers = congressMembersValues.map(
      (cm: ICongressMember) => ({ congressMemberId: cm.id }),
    );
    const eventCounties: any[] = [];

    if (values.eventCounties !== undefined) {
      values.eventCounties.forEach((jd: number) =>
        eventCounties.push({ jurisdictionId: jd }),
      );
    }

    return {
      event,
      eventRevision,
      eventAgencies,
      eventAudience,
      eventStaff,
      eventCongressMembers,
      eventCounties,
    };
  };

  /*
  Creates the API body object with the Form Data and posts the result to the API
  */
  const onFinish = async (values: any) => {
    try {
      setSubmitting(true);

      const data = createFormData(values);
      let resp;

      if (isEdit) {
        resp = await API.post(`event/${id}`, data);
      } else {
        resp = await API.post('event', data);
      }

      if (resp.status === 200) {
        history.push('/events');
      }

      setSubmitting(false);
      message.success('Thank you for your submission!');
    } catch {
      setLoading(false);
      message.error('Looks like something went wrong.');
    }
  };

  const onFinishFailed = async () => {
    message.error('Please fix the errors!');
  };

  // #endregion

  // #region General Component State Functions

  const onCancelClick = () => {
    history.goBack();
  };

  const onDateCheckboxChange = (e: CheckboxChangeEvent) => {
    setDateRequired(!e.target.checked);
  };

  const onAgencyCheckboxChange = (e: CheckboxChangeEvent) => {
    setOtherAgencyRequired(e.target.checked);
  };

  const onAudienceCheckboxChange = (e: CheckboxChangeEvent) => {
    setOtherAudienceRequired(e.target.checked);
  };

  const onJurisidictionRadioChange = (e: RadioChangeEvent) => {
    if (e.target.value === 1) {
      setJurisdictionRequired(true);
    } else {
      setJurisdictionRequired(false);
    }
  };

  const onLocationRadioChange = (e: RadioChangeEvent) => {
    if (e.target.value === 'physical') setPhysicalRequired(true);
    else if (physicalRequired) setPhysicalRequired(false);
  };

  /*
  Adds or removes current jurisdiction to component state on checkbox change
  */
  const onJurisdictionStateChange = (e: CheckboxChangeEvent) => {
    const currentJurisdictionId = e.target.value;

    if (e.target.checked) {
      const newJurisdiction = jurisdictions.find(
        (st: IState) => st.id === currentJurisdictionId,
      );
      if (newJurisdiction !== undefined) {
        setSelectedJurisdictions([...selectedJurisdictions, newJurisdiction]);
      }
    } else {
      const newSelectedJurisdictions = selectedJurisdictions.filter(
        (st: IState) => st.id !== currentJurisdictionId,
      );

      setSelectedJurisdictions(newSelectedJurisdictions);
    }
  };

  /*
  Creates an API get request to find congress members based on current input
  */
  const fetchCongressMembers = async (value: any) => {
    if (value !== '') {
      try {
        setFetchingCongressMembers(true);
        setCongressMembers([]);

        const resp = await API.get('/congress-members/search', {
          params: {
            q: value,
          },
        });

        setCongressMembers(resp.data);
        setFetchingCongressMembers(false);
      } catch {
        setFetchingCongressMembers(false);
      }
    }
  };

  /*
  Debounces fetchCongressMembers based on duration
  */
  const debounceFetchCongressMembers = debounce(fetchCongressMembers, 600);

  /*
  Adds the selected congress member from select options if not already added to congressMemberValues
  */
  const onCongressMemberSelectChange = (value: never[]) => {
    const alreadyAdded = congressMembersValues.some((cm) => cm.id === value[0]);

    if (!alreadyAdded) {
      const newCongressMember = congressMembers.find(
        (cm) => cm.id === value[0],
      );

      if (newCongressMember !== undefined) {
        if (newCongressMember.chamber === 'House') {
          newCongressMember.chamber = 'Rep';
        } else {
          newCongressMember.chamber = 'Sen';
        }

        setCongressMembersValues([...congressMembersValues, newCongressMember]);
      }
    }

    setCongressMembers([]);
    setFetchingCongressMembers(false);
  };

  /*
  Removes the selected congress member from congressMemberValues
  */
  const handleDeleteCongressMember = (congressMemberId: number) => {
    setCongressMembersValues(
      congressMembersValues.filter(
        (currCongressMember) => currCongressMember.id !== congressMemberId,
      ),
    );
  };

  /*
  Resets the state of the current event revision
  */
  const resetEventStates = () => {
    setSelectedJurisdictions([]);
    setCongressMembers([]);
    setCongressMembersValues([]);
    setDateRequired(true);
    setPhysicalRequired(false);
    setOtherAgencyRequired(false);
    setOtherAudienceRequired(false);
    setJurisdictionRequired(false);
  };

  /*
  Gets a new event revision from the server and updates the state
  */
  const fetchNewEventRevision = async (revisionId: number) => {
    setLoading(true);
    resetEventStates();
    await getEventRevision(revisionId);
    setLoading(false);
  };

  // #endregion

  // #region Functions To Render Checkboxes For Attendees And Speaker Section

  /*
  Renders section for one agency in section 7.2.
  */
  function renderOneAdditional(array: IStaff[], currentAgency: string) {
    return (
      <Row key={`${currentAgency}`}>
        <Col span={24}>
          <Row justify="center" className="Event-additional-header">
            <Title level={4} className="Event-additional-header-text">
              {currentAgency}
            </Title>
          </Row>
          <Row gutter={[8, 16]}>
            {array
              .filter((s: IStaff) => s.org === currentAgency)
              .map((st: IStaff) => (
                <Col key={`${st.name}-${st.id}`} span={6}>
                  <Checkbox value={`${st.org}|${st.name}`}>{st.name}</Checkbox>
                </Col>
              ))}
          </Row>
        </Col>
      </Row>
    );
  }

  /*
  Renders all sections in section 7.2.
  */
  function renderAllAdditional(array: IStaff[]) {
    const distinctAgencies = Array.from(
      new Set<string>(array.map((e) => e.org)),
    );
    const renderedList = [];

    for (let i = 0; i < distinctAgencies.length; i += 1) {
      const currentAgency = distinctAgencies[i];
      renderedList.push(renderOneAdditional(array, currentAgency));
    }

    return renderedList;
  }

  /*
  Renders all jurisdiction county checkboxes in 9.2.
  */
  const renderAllJurisdictionCounties = (array: IState[]): JSX.Element[] => {
    const renderedList: JSX.Element[] = [];

    array.forEach((st: IState) => {
      // eslint-disable-next-line no-unused-expressions
      st.counties?.forEach((ct: ICounty) => {
        renderedList.push(
          <Col key={`${ct.name}-${ct.id}`} span={8}>
            <Checkbox key={ct.id} value={ct.id}>
              {ct.name} ({st.abbreviation})
            </Checkbox>
          </Col>,
        );
      });
    });

    return renderedList;
  };

  const displayJurisdictionCounties = renderAllJurisdictionCounties(
    selectedJurisdictions,
  );

  const displayAdditional = renderAllAdditional(additional);

  // #endregion

  // #region Render

  return loading || (isEdit && eventDetails === null) ? (
    <LoadingScreen />
  ) : (
    <>
      <Col span={14}>
        <Title>{isEdit ? 'Edit' : 'Submit'} Event</Title>
        <Text className="Event-description">
          On the Submit Event tab, we ask that you provide several key pieces of
          information about events in support of EHE that your agency is
          planning or participating in.
        </Text>
      </Col>
      {/*
        Form Section
      */}
      <Row>
        <Col span={14} className="Event-form">
          {isEdit && isAdmin && (
            <Row>
              <EventVersions
                eventRevisions={eventRevisions}
                fetchNewEventRevision={fetchNewEventRevision}
              />
            </Row>
          )}
          <Row className="Event-row">
            <Text type="danger" strong>
              * Required Field
            </Text>
          </Row>
          <Form
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            size="large"
            scrollToFirstError
          >
            {/*
            Section 1 - Event Date
            */}
            <Row>
              <Title level={4} className="Event-section-title">
                1. Event Date
                <span className="Event-required-asterisk">*</span>
              </Title>
            </Row>
            <Row>
              <Text type="secondary">
                The date the event took place; if listing participation in a
                conference or other multi-day event, list the actual date of
                involvement, not the conference/event start day or a span of
                dates for a multi-day conference.
              </Text>
            </Row>
            <Row className="Event-input-group" gutter={18} align="middle">
              <Col>
                <Form.Item
                  name="occurredOn"
                  rules={[
                    {
                      type: 'object',
                      required: dateRequired,
                      message: 'Please select a date!',
                    },
                  ]}
                  initialValue={
                    isEdit && eventDetails?.occurredOn !== null
                      ? moment(eventDetails?.occurredOn).utc()
                      : undefined
                  }
                >
                  <DatePicker
                    format="MM/DD/YYYY"
                    placeholder="MM/DD/YYYY"
                    className="Event-datepicker"
                  />
                </Form.Item>
              </Col>
              <Col className="Event-datepicker-or">
                <Text strong>- OR -</Text>
              </Col>
              <Col>
                <Form.Item>
                  <Checkbox
                    onChange={onDateCheckboxChange}
                    defaultChecked={isEdit && eventDetails?.occurredOn === null}
                  >
                    TBD
                  </Checkbox>
                </Form.Item>
              </Col>
            </Row>
            {/*
              Section 2 - Event Location
            */}
            <Row>
              <Title level={4} className="Event-section-title">
                2. Event Location
                <span className="Event-required-asterisk">*</span>
              </Title>
            </Row>
            <Row className="Event-secondary-text">
              <Text type="secondary">
                All events with formats coded as conference call or webinar
                should have locations coded as &quot;Virtual&quot;;
                additionally, any media/interview events without a physical
                location/component should be coded as &quot;Virtual&quot; (like
                a podcast).
              </Text>
            </Row>
            <Form.Item
              name="locationRadio"
              rules={[
                {
                  required: true,
                  message: 'Please select an option!',
                },
              ]}
              initialValue={
                isEdit ? eventDetails?.locationRadioValue : undefined
              }
            >
              <Radio.Group
                className="Event-location-group"
                onChange={onLocationRadioChange}
              >
                <Row gutter={8} align="middle">
                  <Col>
                    <Radio value="physical" className="Event-location-radio" />
                  </Col>
                  <Col span={10}>
                    <Form.Item
                      name="location"
                      rules={[
                        {
                          required: physicalRequired,
                          message: 'Please enter a city!',
                        },
                      ]}
                      initialValue={
                        isEdit && eventDetails?.city !== null
                          ? eventDetails?.city
                          : undefined
                      }
                    >
                      <Input placeholder="City" name="city" />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name="stateAbbreviation"
                      rules={[
                        {
                          required: physicalRequired,
                          message: 'Please select a state!',
                        },
                      ]}
                      initialValue={
                        isEdit && eventDetails?.state !== null
                          ? eventDetails?.state
                          : undefined
                      }
                    >
                      <Select
                        placeholder="State"
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option?.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        {states.map((st: IState) => (
                          <Select.Option key={st.name} value={st.abbreviation}>
                            {st.name}
                          </Select.Option>
                        ))}
                        <Select.Option value="">None</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={8} align="middle">
                  <Col>
                    <Radio value="virtual" className="Event-location-checkbox">
                      Virtual/Other
                    </Radio>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Radio value="tbd">TBD</Radio>
                  </Col>
                </Row>
              </Radio.Group>
            </Form.Item>
            {/*
              Section 3 - Description
            */}
            <Row>
              <Title level={4} className="Event-section-title">
                3. Event Description
                <span className="Event-required-asterisk">*</span>
              </Title>
            </Row>
            <Row className="Event-secondary-text">
              <Text type="secondary">
                Description of event and agency participation; any additional
                details that are not captured by subsequent sections should be
                included here. Note that if an event you are entering in the
                tracker is still &quot;tentative&quot; please indicate that
                here.
              </Text>
            </Row>
            <Row>
              <Col span={24}>
                <Form.Item
                  name="description"
                  rules={[
                    { required: true, message: 'Please enter a description!' },
                  ]}
                  initialValue={
                    isEdit && eventDetails !== null
                      ? `${eventDetails.description}`
                      : undefined
                  }
                >
                  <TextArea
                    rows={8}
                    placeholder="Enter event description..."
                    bordered
                  />
                </Form.Item>
              </Col>
            </Row>
            {/*
              Section 4 - Format
            */}
            <Row>
              <Title level={4} className="Event-section-title">
                4. Format
                <span className="Event-required-asterisk">*</span>
              </Title>
            </Row>
            <Row className="Event-secondary-text">
              <Text type="secondary">
                If desired format is not listed, select closest option from
                drop-down.
              </Text>
            </Row>
            <Row>
              <Col span={12}>
                <Form.Item
                  name="format"
                  rules={[
                    { required: true, message: 'Please select a format!' },
                  ]}
                  initialValue={isEdit ? eventDetails?.format : undefined}
                >
                  <Select placeholder="Format Type" showSearch>
                    {formats.map((fm: IFormat) => (
                      <Select.Option key={fm.type} value={fm.type}>
                        {fm.type}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            {/*
              Section 5 - Audience(s)
            */}
            <Row className="Event-checkbox-group-title">
              <Title level={4} className="Event-section-title">
                5. Audience(s)
                <span className="Event-required-asterisk">*</span>
              </Title>
            </Row>
            <Row className="Event-secondary-text">
              <Text type="secondary">
                The event&apos;s audience; see below for more information.
              </Text>
            </Row>
            <Row>
              <Col span={24} className="Event-checkbox-group">
                <Form.Item
                  name="eventAudience"
                  rules={[
                    { required: true, message: 'Please select an audience!' },
                  ]}
                  initialValue={isEdit ? eventDetails?.audience : undefined}
                >
                  <Checkbox.Group>
                    <Row gutter={[8, 16]}>
                      {audiences.map((a: IAudience) => (
                        <Col key={`${a.name}-${a.id}`} span={8}>
                          <Checkbox key={a.name} value={a.name}>
                            {a.name}
                          </Checkbox>
                        </Col>
                      ))}
                      <Col span={8}>
                        <Row>
                          <Col>
                            <Checkbox
                              value="Other"
                              onChange={onAudienceCheckboxChange}
                            />
                          </Col>
                          <Col span={20} className="Event-checkbox-group-input">
                            <Form.Item
                              name="audienceOther"
                              rules={[
                                {
                                  required: otherAudienceRequired,
                                  message: 'Please enter a value!',
                                },
                              ]}
                              initialValue={
                                isEdit && eventDetails?.otherAudience !== null
                                  ? eventDetails?.otherAudience
                                  : undefined
                              }
                            >
                              <Input placeholder="Other" />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Checkbox.Group>
                </Form.Item>
              </Col>
            </Row>
            {/*
              Section 6 - Participating Agencies
            */}
            <Row className="Event-checkbox-group-title">
              <Title level={4} className="Event-section-title">
                6. Participating Agencies
                <span className="Event-required-asterisk">*</span>
              </Title>
            </Row>
            <Row className="Event-secondary-text">
              <Text type="secondary">
                All agencies who participated in the event.
              </Text>
            </Row>
            <Row>
              <Col span={24} className="Event-checkbox-group">
                <Form.Item
                  name="eventAgencies"
                  rules={[
                    { required: true, message: 'Please select an agency!' },
                  ]}
                  initialValue={isEdit ? eventDetails?.agencies : undefined}
                >
                  <Checkbox.Group>
                    <Row gutter={[8, 16]}>
                      {agencies.map((a: IAgency) => (
                        <Col key={`${a.abbreviation}-${a.id}`} span={8}>
                          <Checkbox key={a.name} value={a.abbreviation}>
                            {a.abbreviation}
                          </Checkbox>
                        </Col>
                      ))}
                      <Col span={8}>
                        <Row>
                          <Col>
                            <Checkbox
                              value="Other"
                              onChange={onAgencyCheckboxChange}
                            />
                          </Col>
                          <Col span={20} className="Event-checkbox-group-input">
                            <Form.Item
                              name="agencyOther"
                              rules={[
                                {
                                  required: otherAgencyRequired,
                                  message: 'Please enter a value!',
                                },
                              ]}
                              initialValue={
                                isEdit && eventDetails?.otherAgency !== null
                                  ? eventDetails?.otherAgency
                                  : undefined
                              }
                            >
                              <Input placeholder="Other" />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Checkbox.Group>
                </Form.Item>
              </Col>
            </Row>
            {/*
              Section 7 - Attendees & Speakers
            */}
            <Row className="Event-checkbox-group-title">
              <Title level={4} className="Event-section-title">
                7. Attendees & Speakers (Must Fill Out One of These)
                <span className="Event-required-asterisk">*</span>
              </Title>
            </Row>
            <Row className="Event-secondary-text">
              <Text type="secondary">List key attendees.</Text>
            </Row>
            <Row>
              <Col span={24} className="Event-checkbox-group">
                <Form.Item
                  name="eventStaff"
                  rules={[
                    { required: true, message: 'Please select an attendee!' },
                  ]}
                  initialValue={isEdit ? eventDetails?.staff : undefined}
                >
                  <Checkbox.Group>
                    <Title level={4} className="Event-section-title">
                      7.1. HHS Principal(s)
                    </Title>
                    <Row className="Event-secondary-text">
                      <Text type="secondary">
                        List HHS principals attending or speaking at the event.
                      </Text>
                    </Row>
                    <Row gutter={[8, 16]}>
                      {principal.map((st: IStaff) => (
                        <Col key={`${st.name}-${st.id}`} span={8}>
                          <Checkbox key={st.id} value={`${st.org}|${st.name}`}>
                            {st.name}
                          </Checkbox>
                        </Col>
                      ))}
                    </Row>
                    <Title level={4} className="Event-section-title">
                      7.2. Additional HHS Staff
                    </Title>
                    <Row className="Event-secondary-text">
                      <Text type="secondary">
                        List additional HHS staff attending or speaking at the
                        event.
                      </Text>
                    </Row>
                    {displayAdditional}
                    <Row justify="center" className="Event-additional-header">
                      <Title level={4} className="Event-additional-header-text">
                        {region.length ? region[1].org : 'HHS Region'}
                      </Title>
                    </Row>
                    <Row gutter={[8, 16]}>
                      {region.map((st: IStaff) => (
                        <Col key={`${st.name}-${st.id}`} span={6}>
                          <Checkbox key={st.id} value={`${st.org}|${st.name}`}>
                            {st.name}
                          </Checkbox>
                        </Col>
                      ))}
                    </Row>
                    <Title
                      level={4}
                      className="Event-additional-checkboxes Event-section-title"
                    >
                      7.3. Additional Federal/Nonfederal Attendees
                    </Title>
                    <Row className="Event-secondary-text">
                      <Text type="secondary">
                        List additional noteworthy attendees. Please include
                        Name, Role/Title, and agency/office affiliation, as
                        appropriate (e.g. John Doe, Program Manager, OASH/OIDP).
                        Do not include Members of Congress here.
                      </Text>
                    </Row>
                    <Form.Item
                      name="additionalAttendees"
                      initialValue={
                        isEdit && eventDetails?.additionalAttendees !== null
                          ? eventDetails?.additionalAttendees
                          : undefined
                      }
                    >
                      <TextArea
                        rows={8}
                        placeholder="Enter all additional attendees..."
                      />
                    </Form.Item>
                  </Checkbox.Group>
                </Form.Item>
              </Col>
            </Row>
            {/*
              Section 8 - Members of Congress Attending
            */}
            <Row className="Event-checkbox-group-title">
              <Title level={4} className="Event-section-title">
                8. Members of Congress Attending
              </Title>
            </Row>
            <Row className="Event-secondary-text">
              <Text type="secondary">
                List each participating Member of Congress; if speaking to
                Congressional staff, list corresponding Member of Congress here,
                and note that it was staff in the event description.
              </Text>
            </Row>
            <Form.Item>
              <Row>
                <Col span={24}>
                  <Select
                    mode="multiple"
                    value={[]}
                    filterOption={false}
                    notFoundContent={
                      fetchingCongressMembers ? <Spin size="small" /> : null
                    }
                    placeholder="Enter Congress member name..."
                    onSearch={debounceFetchCongressMembers}
                    onChange={onCongressMemberSelectChange}
                  >
                    {congressMembers.map((cm: ICongressMember) => (
                      <Select.Option key={cm.id} value={cm.id}>
                        {cm.givenName} {cm.familyName}, {cm.party}-{cm.state}
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
              </Row>
            </Form.Item>
            <Row>
              <div className="Event-congress-member-row">
                {congressMembersValues.map((cm: ICongressMember) => (
                  <div key={cm.id} className="Event-congress-member-button">
                    <p>
                      {cm.chamber}. {cm.givenName} {cm.familyName}, {cm.party}-
                      {cm.state}
                    </p>
                    <Button
                      shape="circle"
                      icon={<CloseOutlined />}
                      className="Event-congress-member-delete"
                      onClick={() => handleDeleteCongressMember(cm.id)}
                    />
                  </div>
                ))}
              </div>
            </Row>
            {/*
              Section 9 - EHE Jurisdiction Visit
            */}
            <Row>
              <Title level={4} className="Event-section-title">
                9. EHE Jurisdiction Visit
                <span className="Event-required-asterisk">*</span>
              </Title>
            </Row>
            <Row>
              <Text type="secondary">
                Restricted to Yes/No; if yes, fill out the following
                corresponding target jurisdiction section.{' '}
                <Text type="secondary" strong>
                  Yes is limited to physical travel
                </Text>{' '}
                (e.g. not a phone call with a representative or congressional
                briefing with a representative of a target jurisdiction) to one
                of the 57 EHE jurisdictions. Only mark &quot;Yes&quot; if the
                event is directly related to the EHE initiative.
              </Text>
            </Row>
            <Row>
              <Form.Item
                name="jurisdictionVisit"
                rules={[
                  { required: true, message: 'Please select an option!' },
                ]}
                initialValue={
                  isEdit ? Number(eventDetails?.jurisdictionVisit) : undefined
                }
              >
                <Radio.Group onChange={onJurisidictionRadioChange}>
                  <Radio value={1}>Yes</Radio>
                  <Radio value={0}>No</Radio>
                </Radio.Group>
              </Form.Item>
            </Row>
            {/*
              Section 9.1. - EHE Target Jurisdiction: State(s)
            */}
            <>
              <Row>
                <Title level={4} className="Event-section-title">
                  9.1. EHE Target Jurisdiction: State(s)
                  {jurisdictionRequired && (
                    <span className="Event-required-asterisk">*</span>
                  )}
                </Title>
              </Row>
              <Row>
                <Text type="secondary" className="Event-secondary-text">
                  If this event was a physical jurisdiction visit, ensure you
                  have selected &quot;Yes&quot; in the preceding question. If
                  this event was not a physical jurisdiction visit but directly
                  involved local jurisdiction stakeholders, please mark the
                  corresponding jurisdiction here.
                </Text>
              </Row>
              <Row>
                <Col span={24} className="Event-checkbox-group">
                  <Form.Item
                    name="states"
                    rules={[
                      {
                        required: jurisdictionRequired,
                        message: 'Please select a state!',
                      },
                    ]}
                    initialValue={
                      isEdit ? eventDetails?.jurisdictionStates : undefined
                    }
                  >
                    <Checkbox.Group>
                      <Row gutter={[8, 16]}>
                        {jurisdictions.map((st: IState) => (
                          <Col key={`${st.abbreviation}-${st.id}`} span={8}>
                            <Checkbox
                              key={st.id}
                              value={st.id}
                              onChange={onJurisdictionStateChange}
                            >
                              {st.name} ({st.abbreviation})
                            </Checkbox>
                          </Col>
                        ))}
                      </Row>
                    </Checkbox.Group>
                  </Form.Item>
                </Col>
              </Row>
            </>
            {/*
              Section 9.2. - EHE Target Jurisdiction: Counties
            */}
            {selectedJurisdictions.length > 0 && (
              <>
                <Row className="Event-checkbox-group-title">
                  <Title level={4} className="Event-section-title">
                    9.2. EHE Target Jurisdiction: Counties
                    <span className="Event-required-asterisk">*</span>
                  </Title>
                </Row>
                <Row>
                  <Text type="secondary" className="Event-secondary-text">
                    If this event was a physical jurisdiction visit, then note
                    each represented target county. If this event was not a
                    physical jurisdiction visit but directly involved local
                    jurisdiction stakeholders, please mark the corresponding
                    jurisdiction here.
                  </Text>
                </Row>
                <Row>
                  <Col span={24} className="Event-checkbox-group">
                    <Form.Item
                      name="eventCounties"
                      rules={[
                        { required: true, message: 'Please select a county!' },
                      ]}
                      initialValue={
                        isEdit ? eventDetails?.jurisdictionCounties : undefined
                      }
                    >
                      <Checkbox.Group style={{ width: '100%' }}>
                        <Row gutter={[8, 16]}>
                          {displayJurisdictionCounties}
                        </Row>
                      </Checkbox.Group>
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
            {/*
              Section 10 - Other Localities Reached
            */}
            <Row>
              <Title
                level={4}
                className="Event-section-title Event-checkbox-group-title"
              >
                10. Other Localities Reached
              </Title>
            </Row>
            <Row className="Event-secondary-text">
              <Text type="secondary">
                Please list any specific non-EHE jurisdictions engaged in this
                event. For example, if you met with the New Mexico HIV Community
                Planning and Action Group, then please list &quot;New
                Mexico&quot;. Multiple locations should be separated by
                semicolon (i.e. New Mexico; Boulder, CO; Fairfax, VA).
              </Text>
            </Row>
            <Row>
              <Col span={24}>
                <Form.Item
                  name="otherLocalitiesReached"
                  initialValue={
                    isEdit && eventDetails?.otherLocalitiesReached !== null
                      ? eventDetails?.otherLocalitiesReached
                      : undefined
                  }
                >
                  <TextArea
                    rows={8}
                    placeholder="Enter other localities reached..."
                    bordered
                  />
                </Form.Item>
              </Col>
            </Row>
            {/*
              Section 11. - Ready, Set, PrEP
            */}
            <Row>
              <Title level={4} className="Event-section-title">
                11. Ready, Set, PrEP
                <span className="Event-required-asterisk">*</span>
              </Title>
            </Row>
            <Row>
              <Text type="secondary">
                Select yes if this event discussed the Ready, Set, PrEP
                initiative.
              </Text>
            </Row>
            <Row>
              <Form.Item
                name="readySetPrep"
                rules={[
                  { required: true, message: 'Please select an option!' },
                ]}
                initialValue={
                  isEdit ? Number(eventDetails?.readySetPrep) : undefined
                }
              >
                <Radio.Group>
                  <Radio value={1}>Yes</Radio>
                  <Radio value={0}>No</Radio>
                </Radio.Group>
              </Form.Item>
            </Row>
            {/*
              Submit Button
            */}
            <Row gutter={16} align="middle">
              <Col>
                <Button type="primary" htmlType="submit" loading={submitting}>
                  {isEdit ? 'Save' : 'Submit'}
                </Button>
              </Col>
              {isEdit && (
                <Col>
                  <Button onClick={onCancelClick}>Cancel</Button>
                </Col>
              )}
              <Col>
                <Text>
                  You are set to automatically follow all events you create or
                  edit.
                </Text>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </>
  );

  // #endregion
};

Event.defaultProps = {
  isEdit: false,
};

export default Event;
